import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type {
  PortalEntityItem,
  PortalStatusItem,
  PortalDataItem,
  PortalProfileItem,
  CustomModuleItem,
  PortalUserItem,
  PortalUserPermissionItem,
  PortalSettingItem,
} from "@ServerTypes/portal/portal.schema";
import type { CostDatabaseData } from "@ServerTypes/costDatabase/costDatabase.schema";
import type { CurrencySymbolItem } from "@ServerTypes/currency/currency.schema";
import type { CategoryItem } from "@ServerTypes/status/status.schema";

// Re-export item types consumed by other files in the codebase
export type {
  PortalEntityItem,
  PortalStatusItem,
  PortalDataItem,
  PortalProfileItem,
  CustomModuleItem,
  PortalUserItem,
  PortalUserPermissionItem,
  PortalSettingItem,
};

/**
 * Every one of these list payloads is effectively `unknown`: each dispatch site hands a field of an
 * axios response over verbatim (src/actions/portalSetting.ts, src/actions/portalSetting.helper.ts),
 * so `Array.isArray` is the only check this reducer can make. The element type comes from the
 * response schema the dispatching call is typed with, not from anything provable here.
 */
function toItemArray<T>(payload: unknown): T[] {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `Array.isArray` narrows to `any[]`, i.e. it proves array-ness and nothing about the elements; their shape is the endpoint response schema each dispatch site types its axios call with (see the comment above)
  return Array.isArray(payload) ? (payload as T[]) : [];
}

const entityListSlice = createSlice({
  name: "entityList",
  initialState: [] as PortalEntityItem[],
  reducers: {
    setEntityList: (_state: PortalEntityItem[], action: PayloadAction<PortalEntityItem[]>) => action.payload,
  },
});

const statusPortalDataSlice = createSlice({
  name: "statusPortalData",
  initialState: [] as PortalStatusItem[],
  reducers: {
    setPortalStatusData: (_state: PortalStatusItem[], action: PayloadAction<PortalStatusItem[]>) => action.payload,
  },
});

const portalDataSlice = createSlice({
  name: "portalData",
  initialState: {} as PortalDataItem,
  reducers: {
    setPortalData: (_state: PortalDataItem, action: PayloadAction<PortalDataItem | null | undefined>) =>
      action.payload ? action.payload : {},
    mergePortalData: (state: PortalDataItem, action: PayloadAction<Partial<PortalDataItem> | null | undefined>) =>
      action.payload ? { ...state, ...action.payload } : {},
  },
});

const profileDataSlice = createSlice({
  name: "profileData",
  initialState: [] as PortalProfileItem[],
  reducers: {
    setPortalProfiles: (
      _state: PortalProfileItem[],
      action: PayloadAction<PortalProfileItem[] | PortalProfileItem | unknown>,
    ) => toItemArray<PortalProfileItem>(action.payload),
  },
});

const portalEntityListSlice = createSlice({
  name: "portalEntityList",
  initialState: [] as PortalEntityItem[],
  reducers: {
    setPortalEntityList: (
      _state: PortalEntityItem[],
      action: PayloadAction<PortalEntityItem[] | PortalEntityItem | unknown>,
    ) => toItemArray<PortalEntityItem>(action.payload),
  },
});

const portalSettingSlice = createSlice({
  name: "portalSetting",
  initialState: [] as PortalSettingItem[],
  reducers: {
    getPortalSetting: (_state: PortalSettingItem[], action: PayloadAction<PortalSettingItem[]>) => action.payload,
  },
});

const customModulesSlice = createSlice({
  name: "customModules",
  initialState: [] as CustomModuleItem[],
  reducers: {
    setCustomModules: (
      _state: CustomModuleItem[],
      action: PayloadAction<CustomModuleItem[] | CustomModuleItem | unknown>,
    ) => toItemArray<CustomModuleItem>(action.payload),
  },
});

const categoryListSlice = createSlice({
  name: "categoryList",
  initialState: [] as CategoryItem[],
  reducers: {
    setCategoryList: (_state: CategoryItem[], action: PayloadAction<CategoryItem[]>) => action.payload,
  },
});

const portalCategoryListSlice = createSlice({
  name: "portalCategoryList",
  initialState: [] as CategoryItem[],
  reducers: {
    setPortalCategoryList: (_state: CategoryItem[], action: PayloadAction<CategoryItem[] | CategoryItem | unknown>) =>
      toItemArray<CategoryItem>(action.payload),
  },
});

const userInPortalSlice = createSlice({
  name: "UserInPortal",
  initialState: [] as PortalUserItem[],
  reducers: {
    getPortalUserList: (_state: PortalUserItem[], action: PayloadAction<PortalUserItem[] | undefined | unknown>) =>
      toItemArray<PortalUserItem>(action.payload),
  },
});

const userCountInPortalSlice = createSlice({
  name: "UserCountInPortal",
  initialState: 0,
  reducers: {
    getPortalUserCount: (_state: number, action: PayloadAction<number | undefined>) => action.payload,
  },
});

const currentUserPermissionsSlice = createSlice({
  name: "currentUserPermissions",
  initialState: [] as PortalUserPermissionItem[],
  reducers: {
    setPortalUserPermission: (_state: PortalUserPermissionItem[], action: PayloadAction<PortalUserPermissionItem[]>) =>
      action.payload,
  },
});

const costDatabaseDataSlice = createSlice({
  name: "costDatabaseData",
  initialState: [] as CostDatabaseData[],
  reducers: {
    getCostDatabaseData: (
      _state: CostDatabaseData[],
      action: PayloadAction<CostDatabaseData[] | CostDatabaseData | null | undefined | unknown>,
    ) => toItemArray<CostDatabaseData>(action.payload),
  },
});

const symbolsDataSlice = createSlice({
  name: "symbolsData",
  initialState: [] as CurrencySymbolItem[],
  reducers: {
    getSymbolsData: (_state: CurrencySymbolItem[], action: PayloadAction<CurrencySymbolItem[]>) => action.payload,
  },
});

const renderHeaderSlice = createSlice({
  name: "renderHeader",
  initialState: false,
  reducers: {
    setRenderHeader: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

// Array-payload and primitive-payload action creators are exported raw —
// `Exact<>` on arrays of objects with heterogeneous optional fields emits
// spurious mismatch errors, and `Exact<>` on primitives has no benefit.
export const { setEntityList: setEntityListAction } = entityListSlice.actions;
export const { setPortalStatusData: setPortalStatusDataAction } = statusPortalDataSlice.actions;
export const { setPortalProfiles: setPortalProfilesAction } = profileDataSlice.actions;
export const { setPortalEntityList: setPortalEntityListAction } = portalEntityListSlice.actions;
export const { getPortalSetting: getPortalSettingAction } = portalSettingSlice.actions;
export const { setCustomModules: setCustomModulesAction } = customModulesSlice.actions;
export const { setCategoryList: setCategoryListAction } = categoryListSlice.actions;
export const { setPortalCategoryList: setPortalCategoryListAction } = portalCategoryListSlice.actions;
export const { getPortalUserList: getPortalUserListAction } = userInPortalSlice.actions;
export const { getPortalUserCount: getPortalUserCountAction } = userCountInPortalSlice.actions;
export const { setPortalUserPermission: setPortalUserPermissionAction } = currentUserPermissionsSlice.actions;
export const { getCostDatabaseData: getCostDatabaseDataAction } = costDatabaseDataSlice.actions;
export const { getSymbolsData: getSymbolsDataAction } = symbolsDataSlice.actions;
export const { setRenderHeader } = renderHeaderSlice.actions;

const { setPortalData: _setPortalDataRaw, mergePortalData: _mergePortalDataRaw } = portalDataSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setPortalDataAction = <T extends Exact<PortalDataItem | null | undefined, T>>(payload: T) =>
  _setPortalDataRaw(payload);
export const mergePortalDataAction = <T extends Exact<Partial<PortalDataItem> | null | undefined, T>>(payload: T) =>
  _mergePortalDataRaw(payload);

export const setEntityList = entityListSlice.reducer;
export const statusPortalData = statusPortalDataSlice.reducer;
export const portalData = portalDataSlice.reducer;
export const profileData = profileDataSlice.reducer;
export const portalEntityList = portalEntityListSlice.reducer;
export const getPortalSetting = portalSettingSlice.reducer;
export const customModules = customModulesSlice.reducer;
export const setCategoryList = categoryListSlice.reducer;
export const setPortalCategoryList = portalCategoryListSlice.reducer;
export const UserInPortal = userInPortalSlice.reducer;
export const UserCountInPortal = userCountInPortalSlice.reducer;
export const currentUserPermissions = currentUserPermissionsSlice.reducer;
export const getCostDatabaseData = costDatabaseDataSlice.reducer;
export const getSymbolsData = symbolsDataSlice.reducer;
export const renderHeader = renderHeaderSlice.reducer;
